import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { FlightsClassTypes } from 'constants/flight'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import SolidPlaneDepartureIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureIcon'
import Pill from 'LuxKit/Pill'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import CharterTileIndividualFlight from './CharterTileIndividualFlight'
import { rem } from 'polished'
import Divider from 'LuxKit/Divider'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import CharterFlightDetailsModal from './CharterFlightDetailsModal'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import FormatCurrency from 'components/Common/FormatCurrency'
import useCharterFlightJourneys from 'hooks/CharterFlights/useCharterFlightJourneys'
import { extractBaggageInfo } from './utils'

const InlineBlock = styled.span`
  display: inline-block;
`

const FlightInfoGrid = styled.div`
  display: grid;
  gap: ${rem(8)};

  grid-template:
    'departingFlight'
    'returningFlight'
    'charterInfo'
    'fareInfo';

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)} ${rem(20)};

    grid-template:
      'departingFlight divider returningFlight'
      'charterInfo charterInfo charterInfo'
      'fareInfo fareInfo fareInfo' / 4fr auto 3fr;
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(8)} ${rem(40)};

    grid-template:
      'departingFlight divider returningFlight fareInfo detailsButton'
      'charterInfo charterInfo charterInfo fareInfo detailsButton' / auto auto auto 1fr auto;
  }
`

const DepartingFlight = styled(CharterTileIndividualFlight)`
  grid-area: departingFlight;

  ${mediaQueryUp.desktop} {
    width: ${rem(290)};
  }
`

const ReturningFlight = styled(CharterTileIndividualFlight)`
  grid-area: returningFlight;

  ${mediaQueryUp.desktop} {
    width: ${rem(234)};
  }
`

const DesktopOnlyDivider = styled(Divider)`
  grid-area: divider;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const FareInfoContainer = styled.div`
  grid-area: fareInfo;

  ${mediaQueryUp.desktop} {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const DetailsButtonContainer = styled.div`
  grid-area: detailsButton;
  display: flex;
  align-items: center;

  ${mediaQueryDown.tablet} {
    display: none;
  }
`

const CharterInfo = styled.div`
  grid-area: charterInfo;
`

const FlexBreakpoint = styled(CSSBreakpoint)`
  display: flex;
`

interface Props {
  route: App.CharterBundleRoute
  departDate?: string
  returnDate?: string
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  flightOrigin?: App.Airport
  feederFlightTotalPrice?: number
}

const defaultRooms: Array<App.Occupants> = [{ adults: 2 }]

function ReturnCharterFlightTile({
  route,
  departDate,
  returnDate,
  occupancies = defaultRooms,
  fareClass,
  flightOrigin,
  feederFlightTotalPrice,
}: Props) {
  const showModal = useContext(ModalContext)

  const routeInfo = CHARTER_FLIGHT_DETAILS[route]

  const {
    departingJourney,
    returningJourney,
    departingFareFamily,
    returningFareFamily,
    fetching: flightsFetching,
  } = useCharterFlightJourneys({
    charterBundleRoute: route,
    departDate,
    returnDate,
    occupancies,
    fareClass,
  })

  const showExactDates = !!(departDate && returnDate)

  const { carryOnBaggage, checkedBaggage } = extractBaggageInfo(departingFareFamily?.fareFamily)

  const openDetailsModal = useCallback(() => {
    if (departingJourney && returningJourney) {
      showModal(<CharterFlightDetailsModal departingJourney={departingJourney} returningJourney={returningJourney} />)
    }
  }, [departingJourney, returningJourney, showModal])

  const needsFeederFlight = !!flightOrigin && flightOrigin.code !== routeInfo.originAirport.airportCode
  const feederFlightPricePerPerson = needsFeederFlight && feederFlightTotalPrice && occupancies ?
    feederFlightTotalPrice / sumUpOccupancies(occupancies) :
    undefined

  return <Pane background="highlight-primary" padding={16} desktopPadding={20}>
    <Group direction="vertical" gap={12}>
      <Group direction="vertical" desktopDirection="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
        <HeadingTextBlock variant="heading5" startIcon={<FlexBreakpoint min="tablet"><SolidPlaneDepartureIcon colour="highlight-secondary" size="M" /></FlexBreakpoint>}>
          <InlineBlock><Colour value="highlight-secondary">Direct flights included in bundle:</Colour></InlineBlock>{' '}
          <InlineBlock>{routeInfo.originAirport.cityDesc} to {routeInfo.destinationAirport.cityDesc} return</InlineBlock>
        </HeadingTextBlock>
        <div>
          <Pill kind="primary" variant="success">Exclusive to {config.brandConfig.name}</Pill>
        </div>
      </Group>

      <Pane border="neutral-six" padding={16} desktopPadding={20}>
        {flightsFetching && <LoadingIndicator inline />}
        {!flightsFetching && !(departingJourney && returningJourney && departingFareFamily && returningFareFamily) && <BodyText variant="medium" colour="critical">No flights found</BodyText>}
        {departingJourney && returningJourney && departingFareFamily && returningFareFamily && <FlightInfoGrid>
          <DepartingFlight journey={departingJourney} showExactDate={showExactDates} segment="departing" route={route} desktopShowLogo />
          <DesktopOnlyDivider kind="primary" direction="vertical" />
          <ReturningFlight journey={returningJourney} showExactDate={showExactDates} segment="returning" route={route} desktopShowLogo={false} />
          <FareInfoContainer>
            <Group direction="horizontal" desktopDirection="vertical" gap={4}>
              {carryOnBaggage && <CaptionTextBlock variant="medium" startIcon={<LineBagIcon colour="highlight-secondary" />}>
                {carryOnBaggage.description}
              </CaptionTextBlock>}
              {checkedBaggage && <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon colour="highlight-secondary" />}>
                {checkedBaggage.description}
              </CaptionTextBlock>}
              <CSSBreakpoint max="tablet">·</CSSBreakpoint>
              <CSSBreakpoint max="tablet">
                <CaptionTextBlock variant="medium"><TextLink weight="regular" onClick={openDetailsModal}>Flight details</TextLink></CaptionTextBlock>
              </CSSBreakpoint>
            </Group>
          </FareInfoContainer>
          <DetailsButtonContainer>
            <TextButton kind="tertiary" onClick={openDetailsModal}>Flight details</TextButton>
          </DetailsButtonContainer>
          <CharterInfo>
            <Caption variant="medium">
              Luxury Escapes Private Charter &nbsp;·&nbsp; Operated by {routeInfo.operatorName}
            </Caption>
          </CharterInfo>
        </FlightInfoGrid>}
      </Pane>

      {needsFeederFlight && flightOrigin && <BodyText variant="medium">
        Add {flightOrigin.name} &gt; {routeInfo.originAirport.cityDesc} flights{' '}
        {!!feederFlightPricePerPerson && <>from <FormatCurrency value={feederFlightPricePerPerson} format="roundedDollar" />/person* </>}
        <Colour value="neutral-three">(available on next step)</Colour>
      </BodyText>}
    </Group>
  </Pane>
}

export default ReturnCharterFlightTile
